import React, { Component, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { isNativePlatform } from './utils/platform';
import { ScrollToTop } from './components/ScrollToTop/ScrollToTop';
import { registerForegroundMessageHandler, getChatPathFromFcmData } from './services/push';
import { apiService } from './services/api';
import { runPendingGuestClaim } from './utils/guestSessionClaim';
import { ModuleGuard } from './context/PlatformFlagsContext';
import Marketplace from './components/Marketplace/Marketplace';
import MarketGroup from './components/Marketplace/MarketGroup';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';

// Dynamic import for Capacitor App plugin (only needed for mobile)
let CapacitorApp: any = null;
if (typeof window !== 'undefined' && isNativePlatform()) {
  import('@capacitor/app').then((module) => {
    CapacitorApp = module.App;
  }).catch(() => {
    // Plugin not available (web build) - this is fine
  });
}
import { Home } from './components/Home';
import { Login, Signup } from './pages/Auth';
import SignupV from './pages/Auth/SignupV';
import OAuthCallback from './pages/Auth/OAuthCallback';
import QrScanLanding from './pages/Qr/QrScanLanding';
import SalonBranchLanding from './pages/Qr/SalonBranchLanding';
import PublicBooking from './pages/Qr/PublicBooking';
import ReviewPage from './pages/Qr/ReviewPage';
import RoomQrLanding from './pages/Qr/RoomQrLanding';
import RoomGuestLanding from './pages/Qr/RoomGuestLanding';
import { Dashboard, BarDashboard } from './pages/Dashboard';
import MatchDetails from './pages/MatchDetails/MatchDetails';
import TournamentLive from './pages/TournamentLive/TournamentLive';
import UserProfile from './pages/Profile/UserProfile';
// Customer Portal - Table Booking
import OrganizerListing from './pages/Customer/Organizers/OrganizerListing';
import OrganizerDetail from './pages/Customer/Organizers/OrganizerDetail';
import TableSelection from './pages/Customer/Tables/TableSelection';
import RoomBooking from './pages/Customer/Rooms/RoomBooking';
import MyBookings from './pages/Customer/Bookings/MyBookings';
import MyWaitlist from './pages/Customer/Waitlist/MyWaitlist';
import BranchTournaments from './pages/Customer/Branches/BranchTournaments';
import BranchHub from './pages/Customer/Branches/BranchHub';
import BookAppointment from './pages/Customer/Appointments/BookAppointment';
import InviteContact from './pages/Customer/Referral/InviteContact';
import ReferralLanding from './pages/Customer/Referral/ReferralLanding';
import Wallet from './pages/Customer/Wallet/Wallet';
import BrowseCategories from './pages/Customer/Categories/BrowseCategories';
import Activities from './pages/Customer/Activities/Activities';
import ShoppingRequestChatPage from './pages/Customer/Chat/ShoppingRequestChatPage';
import CartOrderDetailPage from './pages/Customer/CartOrders/CartOrderDetailPage';
import CartOrderListPage from './pages/Customer/CartOrders/CartOrderListPage';
// Order Management (v1 contract)
import OrdersListPage from './pages/Customer/Orders/OrdersListPage';
import OrderDetailPage from './pages/Customer/Orders/OrderDetailPage';
import BranchChatPage from './pages/Customer/Orders/BranchChatPage';
// Classifieds Marketplace (Dubizzle-style) — distinct from the mediator-groups Marketplace above
import MarketHome from './pages/Customer/Market/MarketHome';
import BranchGroups from './pages/Customer/BranchGroups/BranchGroups';
import MarketBrowse from './pages/Customer/Market/MarketBrowse';
import MarketListingDetail from './pages/Customer/Market/MarketListingDetail';
import MarketPost from './pages/Customer/Market/MarketPost';
import MarketMyListings from './pages/Customer/Market/MarketMyListings';
import MarketSaved from './pages/Customer/Market/MarketSaved';
import MarketMessages from './pages/Customer/Market/MarketMessages';
import MarketProviders from './pages/Customer/Market/MarketProviders';
// Social Feed
import PostFeed from './pages/SocialFeed/PostFeed/PostFeed';
import PostManagement from './pages/SocialFeed/PostManagement/PostManagement';
import SharedPostView from './pages/SocialFeed/SharedPostView/SharedPostView';
// Legal (public)
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import CookiePolicy from './pages/Legal/CookiePolicy';
import VerifyEmail from './pages/Legal/VerifyEmail';
import CookieConsent from './components/CookieConsent/CookieConsent';

// Sets data-portal on <html>: the registered-customer app gets the professional LIGHT theme; the QR
// guest portals (/t, /b, /r, /rg) keep the dark billiards ground. One place, route-driven.
const PortalTheme = () => {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    const isQr = p.startsWith('/t/') || p.startsWith('/b/') || p.startsWith('/r/') || p.startsWith('/rg/');
    document.documentElement.setAttribute('data-portal', isQr ? 'qr' : 'app');
  }, [location.pathname]);
  return null;
};

// Component to handle sessionStorage redirect from backend and last-path restore
const SessionStorageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return;

    // 1) Backend may store the original path in sessionStorage when redirecting to /
    const storedPath = sessionStorage.getItem('redirectPath') ||
                      sessionStorage.getItem('originalPath') ||
                      sessionStorage.getItem('targetPath') ||
                      sessionStorage.getItem('route');
    if (storedPath) {
      sessionStorage.removeItem('redirectPath');
      sessionStorage.removeItem('originalPath');
      sessionStorage.removeItem('targetPath');
      sessionStorage.removeItem('route');
      console.log('[App] Redirecting from sessionStorage to:', storedPath);
      navigate(storedPath, { replace: true });
      return;
    }

    // 2) If user is logged in, send them to last visited page (maintain session + last page)
    if (apiService.isAuthenticated()) {
      const lastPath = apiService.getLastPath();
      console.log('[App] Authenticated user on /, redirecting to last path:', lastPath);
      navigate(lastPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
};

// Once the visitor is registered, auto-claim a referral they arrived on before signing up.
const PendingReferralClaim = () => {
  const location = useLocation();
  useEffect(() => {
    if (!apiService.isAuthenticated()) return;
    if (location.pathname.startsWith('/refer/')) return; // the landing page claims itself
    let raw: string | null = null;
    try { raw = localStorage.getItem('pendingReferral'); } catch { return; }
    if (!raw) return;
    let p: any;
    try { p = JSON.parse(raw); } catch { try { localStorage.removeItem('pendingReferral'); } catch { /* ignore */ } return; }
    if (!p?.branchId || !p?.code) { try { localStorage.removeItem('pendingReferral'); } catch { /* ignore */ } return; }
    apiService.recordReferral(p.code, p.branchId).catch(() => {}).finally(() => { try { localStorage.removeItem('pendingReferral'); } catch { /* ignore */ } });
  }, [location.pathname]);
  return null;
};

// Persist current path (customer portal only) so reopening the link or FCM click restores last page
const LastPathPersist = () => {
  const location = useLocation();
  useEffect(() => {
    if (!apiService.isAuthenticated()) return;
    const path = location.pathname + location.search;
    apiService.setLastPath(path);
  }, [location.pathname, location.search]);
  return null;
};

// When FCM chat message arrives in foreground, navigate to the correct chat (see docs/FCM_WEB_PUSH_FRONTEND_INTEGRATION_PARAMS.md §3.3)
const FcmForegroundNavigate = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent).detail;
      const data = payload?.data;
      if (data?.type) {
        const path = getChatPathFromFcmData(data);
        if (path !== "/") navigate(path);
      }
    };
    window.addEventListener("fcm-foreground-message", handler);
    return () => window.removeEventListener("fcm-foreground-message", handler);
  }, [navigate]);
  return null;
};

// After a QR guest logs in to "save their visit", carry their session onto the registered account.
// Runs on navigation; the runner itself no-ops unless a pending claim exists and the user is now a
// logged-in (non-guest) customer. See utils/guestSessionClaim.
const GuestSessionClaimRunner = () => {
  const location = useLocation();
  useEffect(() => {
    void runPendingGuestClaim();
  }, [location.pathname]);
  return null;
};

class App extends Component {
  async componentDidMount() {
    // FCM foreground messages: dispatch so any component can show a toast (see docs/FCM_WEB_PUSH_FRONTEND_INTEGRATION.md)
    registerForegroundMessageHandler((payload) => {
      console.log('[FCM] Foreground message received', payload);
      window.dispatchEvent(new CustomEvent('fcm-foreground-message', { detail: payload }));
    });

    // Handle deep links for OAuth callbacks on mobile
    if (isNativePlatform()) {
      try {
        // Dynamically import CapacitorApp if not already loaded
        if (!CapacitorApp) {
          const module = await import('@capacitor/app');
          CapacitorApp = module.App;
        }
        
        if (CapacitorApp) {
          CapacitorApp.addListener('appUrlOpen', (data: any) => {
            console.log('[App] Deep link received:', data.url);
            
            // Parse the URL to extract the OAuth callback
            const url = new URL(data.url);
            
            // If it's an OAuth callback, navigate to the callback route
            if (url.pathname.includes('oauth/callback') || url.searchParams.has('code')) {
              // Extract the full URL and navigate
              const callbackUrl = url.pathname + url.search;
              window.location.href = callbackUrl;
            }
          });
        }
      } catch (error) {
        // Plugin not available (web build) - this is expected
        console.log('[App] Capacitor App plugin not available (web build)');
      }
    }
  }

  componentWillUnmount() {
    // Remove listeners when component unmounts
    if (isNativePlatform() && CapacitorApp) {
      try {
        CapacitorApp.removeAllListeners();
      } catch (error) {
        // Ignore errors during cleanup
      }
    }
  }

  render() {
    return (
      <Router>
        <ScrollToTop />
        <PortalTheme />
        <SessionStorageRedirect />
        <LastPathPersist />
        <FcmForegroundNavigate />
        <PendingReferralClaim />
        <GuestSessionClaimRunner />
        {/* Global safety net: a render throw anywhere in the route tree shows the friendly retry
            fallback instead of white-screening the whole app. Per-page boundaries (e.g. the QR
            pages) still apply as nested boundaries and catch closer to the crash first. */}
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/signupv" element={<SignupV />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          {/* Public legal pages (no auth) */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* Public: scan a table QR to call staff (no login) */}
          <Route path="/t/:token" element={<QrScanLanding />} />
          <Route path="/b/:token" element={<SalonBranchLanding />} />
          <Route path="/book/:token" element={<PublicBooking />} />
          <Route path="/review/:refType/:refId" element={<ReviewPage />} />
          <Route path="/r/:token" element={<RoomQrLanding />} />
          <Route path="/rg/:token" element={<RoomGuestLanding />} />
          <Route path="/customer/friends" element={<ModuleGuard flag="social"><Dashboard /></ModuleGuard>} />
          <Route path="/customer/marketplace" element={<ModuleGuard flag="marketplace"><Marketplace /></ModuleGuard>} />
          <Route path="/customer/marketplace/:groupId" element={<ModuleGuard flag="marketplace"><MarketGroup /></ModuleGuard>} />
          {/* Classifieds Marketplace (NEW) — /customer/market (do not confuse with /customer/marketplace) */}
          <Route path="/customer/market" element={<MarketHome />} />
          <Route path="/customer/market/browse" element={<MarketBrowse />} />
          <Route path="/customer/market/listing/:id" element={<MarketListingDetail />} />
          <Route path="/customer/market/post" element={<MarketPost />} />
          <Route path="/customer/market/my" element={<MarketMyListings />} />
          <Route path="/customer/market/saved" element={<MarketSaved />} />
          <Route path="/customer/market/messages" element={<MarketMessages />} />
          <Route path="/customer/market/providers" element={<MarketProviders />} />
          <Route path="/dashboard/organizer/:organizerId" element={<BarDashboard />} />
          <Route path="/match/:matchId" element={<ModuleGuard flag="tournaments"><MatchDetails /></ModuleGuard>} />
          <Route path="/tournament/:tournamentId" element={<ModuleGuard flag="tournaments"><TournamentLive /></ModuleGuard>} />
          <Route path="/profile" element={<UserProfile />} />
          {/* Customer Portal - Table Booking Routes */}
          <Route path="/customer/organizers" element={<OrganizerListing />} />
          <Route path="/customer/categories" element={<BrowseCategories />} />
          <Route path="/customer/organizers/:organizerId" element={<OrganizerDetail />} />
          <Route path="/refer/:branchId/:code" element={<ReferralLanding />} />
          <Route path="/customer/invite/:branchId" element={<InviteContact />} />
          <Route path="/customer/wallet/:branchId" element={<Wallet />} />
          <Route path="/customer/invite" element={<InviteContact />} />
          <Route path="/customer/organizers/:organizerId/branches/:branchId" element={<BranchHub />} />
          <Route path="/customer/organizers/:organizerId/branches/:branchId/tables" element={<TableSelection />} />
          <Route path="/customer/organizers/:organizerId/branches/:branchId/rooms" element={<RoomBooking />} />
          <Route path="/customer/organizers/:organizerId/branches/:branchId/appointments" element={<BookAppointment />} />
          <Route path="/customer/organizers/:organizerId/branches/:branchId/tournaments" element={<ModuleGuard flag="tournaments"><BranchTournaments /></ModuleGuard>} />
          <Route path="/customer/bookings" element={<MyBookings />} />
          <Route path="/customer/waitlist" element={<MyWaitlist />} />
          <Route path="/chat/shopping-request/:requestId" element={<ShoppingRequestChatPage />} />
          <Route path="/cart-orders" element={<CartOrderListPage />} />
          <Route path="/cart-orders/:cartOrderId" element={<CartOrderDetailPage />} />
          <Route path="/customer/activities" element={<ModuleGuard flag="social"><Activities /></ModuleGuard>} />
          {/* Order Management (v1 contract) */}
          <Route path="/customer/branch-groups" element={<BranchGroups />} />
          <Route path="/customer/orders" element={<OrdersListPage />} />
          <Route path="/customer/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/customer/branches/:branchId/chat" element={<BranchChatPage />} />
          {/* Social Feed Routes */}
          <Route path="/customer/social/feed" element={<ModuleGuard flag="social"><PostFeed /></ModuleGuard>} />
          <Route path="/customer/social/posts" element={<ModuleGuard flag="social"><PostManagement /></ModuleGuard>} />
          <Route path="/customer/social/posts/:postId" element={<ModuleGuard flag="social"><SharedPostView /></ModuleGuard>} />
        </Routes>
        </ErrorBoundary>
        <CookieConsent />
      </Router>
    );
  }
}

export default App;
